import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MessageSquare, Pencil, Trash2, Plus, History, Check, RotateCcw, Loader2, X, Send, Lock } from 'lucide-react';
import { auditReports } from '../../services/api';

// The body of an audit report with its collaboration tools: every finding has a
// discussion thread open to the teams that see the patient, and the report's
// responsável (who generated it) edits the content. The Word file and this
// preview are rebuilt by the server on each saved edit.

const GENERAL = 'general';
const COMMENTS_POLL_MS = 20000;

const SEVERITIES = [
  'CRÍTICO', 'ALTO', 'MÉDIO-ALTO', 'MÉDIO', 'BAIXO',
  'BAIXO — pendente de verificação', 'PENDENTE DE ACOMPANHAMENTO', 'PENDENTE DE VERIFICAÇÃO'
];

const PRIORITIES = [
  ['imediata', 'Prioridade imediata'],
  ['alta', 'Prioridade alta'],
  ['media', 'Prioridade média'],
  ['acompanhamento_continuo', 'Acompanhamento contínuo']
];

const ROLE_LABELS = {
  admin: 'Administrador', super_admin: 'Administrador', supervisor: 'Supervisor', therapist: 'Terapeuta',
  doctor: 'Médico', operations: 'Operações', demo: 'Demo'
};

const SANS = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const apiError = (err, fallback) => err?.response?.data?.message || fallback;

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const personName = (person) => (person ? `${person.firstName} ${person.lastName}`.trim() : 'Usuário removido');

/**
 * Splits the report HTML into what comes before the findings, one piece per
 * finding (each starts at its h3) and what comes after, so a thread can sit
 * under each finding. Returns null when the HTML does not match the findings
 * (the report is then shown whole, with the general discussion only).
 */
export function splitReportHtml(html, findings) {
  if (!html || !findings || !findings.length) return null;
  const nodes = Array.from(new DOMParser().parseFromString(html, 'text/html').body.children);
  const before = [];
  const pieces = [];
  const after = [];
  nodes.forEach((node) => {
    if (after.length || (pieces.length && node.tagName === 'H1')) after.push(node.outerHTML);
    else if (node.tagName === 'H3') pieces.push([node.outerHTML]);
    else if (pieces.length) pieces[pieces.length - 1].push(node.outerHTML);
    else before.push(node.outerHTML);
  });
  if (pieces.length !== findings.length) return null;
  return {
    before: before.join(''),
    findings: pieces.map((piece, i) => ({ finding: findings[i], html: piece.join('') })),
    after: after.join('')
  };
}

// ---------------------------------------------------------------- discussion

const Comment = ({ comment, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    const ok = await onSave(comment.id, draft.trim());
    setBusy(false);
    if (ok) setEditing(false);
  };

  return (
    <div className="py-2.5" style={{ opacity: comment.resolvedAt ? 0.7 : 1 }}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          <span className="font-semibold text-gray-800">{personName(comment.author)}</span>
          {comment.author && ROLE_LABELS[comment.author.role] && <span> ({ROLE_LABELS[comment.author.role]})</span>}
          <span> · {formatDateTime(comment.createdAt)}</span>
          {comment.editedAt && <span className="italic"> · editado</span>}
        </div>
        {!editing && (comment.canEdit || comment.canDelete) && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {comment.canEdit && (
              <button onClick={() => { setDraft(comment.body); setEditing(true); }} className="p-1 text-gray-300 hover:text-gray-600 rounded" title="Alterar comentário">
                <Pencil size={13} />
              </button>
            )}
            {comment.canDelete && (
              <button
                onClick={() => { if (window.confirm('Excluir este comentário?')) onDelete(comment.id); }}
                className="p-1 text-gray-300 hover:text-red-600 rounded"
                title="Excluir comentário"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={4000}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <div className="flex justify-end gap-2 mt-1">
            <button onClick={() => setEditing(false)} className="px-3 py-1 text-xs text-gray-500 hover:text-gray-800">Cancelar</button>
            <button onClick={save} disabled={busy || !draft.trim()} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50">
              Salvar
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap break-words" style={{ lineHeight: 1.5 }}>{comment.body}</p>
      )}
    </div>
  );
};

const Thread = ({ anchorKey, title, comments, actions, canPost = true, alwaysOpen = false }) => {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const active = comments.filter((c) => !c.resolvedAt);
  const resolved = comments.filter((c) => c.resolvedAt);
  const expanded = alwaysOpen || open || active.length > 0;

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    const ok = await actions.post(anchorKey, draft.trim());
    setSending(false);
    if (ok) setDraft('');
  };

  const label = active.length
    ? `${active.length} ${active.length === 1 ? 'comentário' : 'comentários'}`
    : resolved.length ? 'Discussão resolvida' : 'Comentar';

  return (
    <div
      data-thread={anchorKey}
      style={{ fontFamily: SANS, margin: '0.75rem 0 1.5rem 0', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc' }}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <button
          onClick={() => setOpen(!expanded)}
          className="flex items-center gap-2 text-xs font-semibold"
          style={{ color: active.length ? '#1d4ed8' : resolved.length ? '#15803d' : '#64748b' }}
        >
          {resolved.length && !active.length ? <Check size={14} /> : <MessageSquare size={14} />}
          {title ? `${title} — ${label}` : label}
        </button>
        {active.length > 0 && (
          <button onClick={() => actions.resolve(anchorKey, true)} className="flex items-center gap-1 text-xs font-medium text-green-700 hover:text-green-900">
            <Check size={13} /> Resolver
          </button>
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-3" style={{ borderTop: '1px solid #e2e8f0' }}>
          {resolved.length > 0 && (
            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setShowResolved(!showResolved)} className="text-xs text-gray-500 hover:text-gray-800">
                {showResolved ? 'Ocultar' : 'Mostrar'} {resolved.length} {resolved.length === 1 ? 'comentário resolvido' : 'comentários resolvidos'}
              </button>
              <button onClick={() => actions.resolve(anchorKey, false)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800">
                <RotateCcw size={12} /> Reabrir
              </button>
            </div>
          )}
          <div className="divide-y divide-gray-200">
            {(showResolved ? comments : active).map((c) => (
              <Comment key={c.id} comment={c} onSave={actions.edit} onDelete={actions.remove} />
            ))}
          </div>
          {canPost && (
            <div className="flex items-end gap-2 pt-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={draft.includes('\n') || draft.length > 90 ? 3 : 1}
                maxLength={4000}
                placeholder="Escreva um comentário..."
                className="flex-1 text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <button
                onClick={send}
                disabled={sending || !draft.trim()}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-40"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- editing

const Modal = ({ title, onClose, children, footer, wide }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" style={{ backdropFilter: 'blur(4px)', fontFamily: SANS }}>
    <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} flex flex-col`} style={{ maxHeight: '90vh' }}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded" title="Fechar"><X size={18} /></button>
      </div>
      <div className="px-6 py-4 overflow-y-auto">{children}</div>
      {footer && <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100">{footer}</div>}
    </div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block mb-4">
    <span className="block text-xs font-semibold text-gray-700 mb-1">{label}</span>
    {children}
    {hint && <span className="block text-xs text-gray-400 mt-1">{hint}</span>}
  </label>
);

const inputClass = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200';

const SaveFooter = ({ onClose, onSave, saving, disabled, error, left }) => (
  <>
    <div className="flex items-center gap-3 min-w-0">
      {left}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
      <button onClick={onSave} disabled={saving || disabled} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
        {saving && <Loader2 size={14} className="animate-spin" />} Salvar
      </button>
    </div>
  </>
);

/** Edit, add (finding = null) or remove one finding. */
const FindingModal = ({ finding, onClose, onSave, saving, error }) => {
  const [form, setForm] = useState({
    titulo: finding?.titulo || '',
    severidade: finding?.severidade || 'MÉDIO',
    observacao: finding?.observacao || '',
    recomendacao: finding?.recomendacao || ''
  });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const valid = form.titulo.trim() && form.observacao.trim();

  return (
    <Modal
      title={finding ? 'Editar achado' : 'Adicionar achado'}
      onClose={onClose}
      footer={(
        <SaveFooter
          onClose={onClose}
          saving={saving}
          disabled={!valid}
          error={error}
          onSave={() => onSave(form)}
          left={finding && (
            <button
              onClick={() => { if (window.confirm('Remover este achado do relatório?')) onSave(null); }}
              className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800"
            >
              <Trash2 size={14} /> Remover achado
            </button>
          )}
        />
      )}
    >
      <Field label="Título">
        <input value={form.titulo} onChange={set('titulo')} maxLength={300} className={inputClass} />
      </Field>
      <Field label="Severidade" hint="Os achados são reordenados por severidade e o resumo executivo é recontado automaticamente.">
        <select value={form.severidade} onChange={set('severidade')} className={inputClass}>
          {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Observação">
        <textarea value={form.observacao} onChange={set('observacao')} rows={5} className={inputClass} />
      </Field>
      <Field label="Recomendação">
        <textarea value={form.recomendacao} onChange={set('recomendacao')} rows={3} className={inputClass} />
      </Field>
      {finding && finding.citacoes?.length > 0 && (
        <p className="text-xs text-gray-400">
          Os trechos citados dos documentos ({finding.citacoes.length}) são a evidência do achado e não podem ser alterados.
        </p>
      )}
    </Modal>
  );
};

const toLines = (list) => (list || []).map((item) => (typeof item === 'string' ? item : item.texto)).join('\n');
const fromLines = (text) => text.split('\n').map((line) => line.trim()).filter(Boolean);

/** Edit the sections other than the findings. Lists are one item per line. */
const SectionsModal = ({ content, onClose, onSave, saving, error }) => {
  const [form, setForm] = useState({
    escopo_e_limitacoes: content.escopo_e_limitacoes || '',
    destaques_resumo: content.destaques_resumo || '',
    pontos_positivos: toLines(content.pontos_positivos),
    perguntas_para_clinica: toLines(content.perguntas_para_clinica),
    ...Object.fromEntries(PRIORITIES.map(([key]) => [key, toLines(content.recomendacoes_priorizadas?.[key])]))
  });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = () => {
    // A recommendation keeps its "(Achado n)" references when its wording is
    // unchanged, or when it is still on the same line of an equally long list.
    const recs = Object.fromEntries(PRIORITIES.map(([key]) => {
      const previous = content.recomendacoes_priorizadas?.[key] || [];
      const lines = fromLines(form[key]);
      return [key, lines.map((texto, i) => {
        const same = previous.find((r) => r.texto === texto) || (lines.length === previous.length ? previous[i] : null);
        return { texto, achados: same ? same.achados : [] };
      })];
    }));
    onSave({
      escopo_e_limitacoes: form.escopo_e_limitacoes,
      destaques_resumo: form.destaques_resumo,
      pontos_positivos: fromLines(form.pontos_positivos),
      recomendacoes_priorizadas: recs,
      perguntas_para_clinica: fromLines(form.perguntas_para_clinica)
    });
  };

  return (
    <Modal title="Editar seções do relatório" onClose={onClose} wide footer={<SaveFooter onClose={onClose} onSave={save} saving={saving} error={error} />}>
      <Field label="1. Escopo e limitações">
        <textarea value={form.escopo_e_limitacoes} onChange={set('escopo_e_limitacoes')} rows={4} className={inputClass} />
      </Field>
      <Field label="2. Resumo executivo — destaques" hint="A contagem de achados por severidade é escrita automaticamente antes deste texto.">
        <textarea value={form.destaques_resumo} onChange={set('destaques_resumo')} rows={3} className={inputClass} />
      </Field>
      <Field label="4. Pontos positivos" hint="Um item por linha.">
        <textarea value={form.pontos_positivos} onChange={set('pontos_positivos')} rows={4} className={inputClass} />
      </Field>
      {PRIORITIES.map(([key, label]) => (
        <Field key={key} label={`5. Recomendações — ${label}`} hint="Uma recomendação por linha.">
          <textarea value={form[key]} onChange={set(key)} rows={3} className={inputClass} />
        </Field>
      ))}
      <Field label="6. Perguntas para a clínica" hint="Uma pergunta por linha.">
        <textarea value={form.perguntas_para_clinica} onChange={set('perguntas_para_clinica')} rows={4} className={inputClass} />
      </Field>
    </Modal>
  );
};

const HistoryModal = ({ reportId, onClose }) => {
  const [revisions, setRevisions] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    auditReports.listRevisions(reportId)
      .then((res) => setRevisions(res.data.data || []))
      .catch((err) => setError(apiError(err, 'Erro ao carregar o histórico.')));
  }, [reportId]);

  return (
    <Modal title="Histórico de alterações" onClose={onClose} wide>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!revisions && !error && <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carregando...</p>}
      {revisions && revisions.length === 0 && (
        <p className="text-sm text-gray-500">Nenhuma alteração: o relatório está como o GiA o gerou.</p>
      )}
      {revisions && revisions.map((revision) => (
        <div key={revision.id} className="mb-5">
          <div className="text-xs text-gray-500 mb-2">
            <span className="font-semibold text-gray-800">Versão {revision.version}</span> · {personName(revision.editor)} · {formatDateTime(revision.createdAt)}
          </div>
          {revision.changes.map((change, i) => (
            <div key={i} className="mb-2 p-3 rounded-lg border border-gray-100 bg-gray-50 text-sm">
              <div className="text-xs font-semibold text-gray-700 mb-1">{change.label} — {change.field}</div>
              {change.before && <div className="text-red-700 whitespace-pre-wrap break-words" style={{ textDecoration: 'line-through' }}>{change.before}</div>}
              {change.after && <div className="text-green-800 whitespace-pre-wrap break-words">{change.after}</div>}
            </div>
          ))}
        </div>
      ))}
    </Modal>
  );
};

// ---------------------------------------------------------------- body

const Html = ({ html, className }) => <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;

const AuditReportBody = ({ report, html, onReportChanged }) => {
  const [comments, setComments] = useState([]);
  const [error, setError] = useState(null);
  const [editingFinding, setEditingFinding] = useState(null); // finding | 'new' | null
  const [editingSections, setEditingSections] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const permissions = report.permissions || {};
  const content = report.content;
  const findings = useMemo(() => content?.achados || [], [content]);
  const split = useMemo(() => splitReportHtml(html, findings), [html, findings]);

  const fetchComments = useCallback(async () => {
    try {
      const res = await auditReports.listComments(report.id);
      setComments(res.data.data || []);
    } catch (err) {
      console.error('Error fetching audit report comments:', err);
    }
  }, [report.id]);

  // Other people's comments appear without a reload.
  useEffect(() => {
    fetchComments();
    const timer = setInterval(fetchComments, COMMENTS_POLL_MS);
    return () => clearInterval(timer);
  }, [fetchComments]);

  const run = async (request, fallback) => {
    try {
      setError(null);
      await request();
      await fetchComments();
      return true;
    } catch (err) {
      console.error(fallback, err);
      setError(apiError(err, fallback));
      return false;
    }
  };

  const actions = {
    post: (anchorKey, body) => run(() => auditReports.addComment(report.id, { anchorKey, body }), 'Erro ao enviar o comentário.'),
    edit: (commentId, body) => run(() => auditReports.updateComment(commentId, body), 'Erro ao alterar o comentário.'),
    remove: (commentId) => run(() => auditReports.deleteComment(commentId), 'Erro ao excluir o comentário.'),
    resolve: (anchorKey, resolved) => run(() => auditReports.resolveThread(report.id, { anchorKey, resolved }), 'Erro ao atualizar a discussão.')
  };

  const saveContent = async (next) => {
    try {
      setSaving(true);
      setSaveError(null);
      await auditReports.updateContent(report.id, { version: report.contentVersion, content: next });
      await onReportChanged();
      setEditingFinding(null);
      setEditingSections(false);
    } catch (err) {
      console.error('Error saving audit report:', err);
      setSaveError(apiError(err, 'Erro ao salvar as alterações.'));
    } finally {
      setSaving(false);
    }
  };

  const saveFinding = (form) => {
    let achados;
    if (editingFinding === 'new') achados = [...findings, form];
    else if (form === null) achados = findings.filter((f) => f.id !== editingFinding.id);
    else achados = findings.map((f) => (f.id === editingFinding.id ? { ...f, ...form } : f));
    saveContent({ ...content, achados });
  };

  const closeModals = () => { setEditingFinding(null); setEditingSections(false); setSaveError(null); };

  const byAnchor = (key) => comments.filter((c) => c.anchorKey === key);
  const findingIds = new Set(findings.map((f) => f.id));
  // Discussions of findings that were later removed from the report stay visible.
  const orphanKeys = [...new Set(comments.map((c) => c.anchorKey).filter((key) => key !== GENERAL && (!split || !findingIds.has(key))))];
  const orphanTitle = (key) => {
    const finding = findings.find((f) => f.id === key);
    const label = finding ? finding.titulo : comments.find((c) => c.anchorKey === key)?.anchorLabel;
    return finding ? `Achado: ${label}` : `Achado removido: ${label || 'sem título'}`;
  };

  return (
    <div>
      <style>{`.audit-doc-v2 .audit-doc-rest h1:first-child { margin-top: 2.5rem; }`}</style>

      {/* Who edits, and the editing tools */}
      {content && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 px-4 py-2.5 rounded-lg" style={{ fontFamily: SANS, background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
          <span className="flex items-center gap-2 text-xs text-gray-600">
            {permissions.canEdit ? <Pencil size={13} /> : <Lock size={13} />}
            {permissions.canEdit
              ? 'Você é o responsável por este relatório: só você pode editá-lo.'
              : report.validatedAt
                ? 'Relatório validado: a edição está bloqueada. Os comentários continuam abertos.'
                : `Só ${personName(report.uploader)}, responsável pelo relatório, pode editá-lo. Deixe sua opinião nos comentários.`}
            {report.lastEditedAt && (
              <span className="text-gray-400 ml-1">Última edição: {formatDateTime(report.lastEditedAt)}{report.lastEditor ? ` por ${personName(report.lastEditor)}` : ''}.</span>
            )}
          </span>
          <span className="flex items-center gap-2">
            {permissions.canEdit && (
              <button onClick={() => setEditingSections(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50">
                <Pencil size={12} /> Editar seções
              </button>
            )}
            <button onClick={() => setShowHistory(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50">
              <History size={12} /> Histórico
            </button>
          </span>
        </div>
      )}

      {error && (
        <div className="mb-4 px-4 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg" style={{ fontFamily: SANS }}>{error}</div>
      )}

      <div className="audit-doc-v2">
        {split ? (
          <>
            <Html html={split.before} />
            {split.findings.map(({ finding, html: findingHtml }) => (
              <div key={finding.id} data-finding={finding.id} style={{ position: 'relative' }}>
                {permissions.canEdit && (
                  <button
                    onClick={() => setEditingFinding(finding)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50"
                    style={{ position: 'absolute', top: 0, right: 0, fontFamily: SANS, zIndex: 1 }}
                  >
                    <Pencil size={12} /> Editar
                  </button>
                )}
                <Html html={findingHtml} />
                <Thread anchorKey={finding.id} comments={byAnchor(finding.id)} actions={actions} />
              </div>
            ))}
            {permissions.canEdit && (
              <button
                onClick={() => setEditingFinding('new')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100"
                style={{ fontFamily: SANS }}
              >
                <Plus size={13} /> Adicionar achado
              </button>
            )}
            <Html html={split.after} className="audit-doc-rest" />
          </>
        ) : (
          <>
            <Html html={html} />
            {permissions.canEdit && findings.length === 0 && (
              <button
                onClick={() => setEditingFinding('new')}
                className="flex items-center gap-1.5 px-3 py-1.5 mt-4 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100"
                style={{ fontFamily: SANS }}
              >
                <Plus size={13} /> Adicionar achado
              </button>
            )}
          </>
        )}
      </div>

      {/* Discussion of the report as a whole, and of findings no longer in it */}
      <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '2px solid #e2e8f0', fontFamily: SANS }}>
        <h4 className="text-sm font-semibold text-gray-900 mb-1">Discussão geral</h4>
        <p className="text-xs text-gray-500 mb-2">Visível para todos que têm acesso aos relatórios de auditoria deste paciente.</p>
        <Thread anchorKey={GENERAL} comments={byAnchor(GENERAL)} actions={actions} alwaysOpen />
        {orphanKeys.map((key) => (
          <Thread key={key} anchorKey={key} title={orphanTitle(key)} comments={byAnchor(key)} actions={actions} canPost={findingIds.has(key)} />
        ))}
      </div>

      {editingFinding && (
        <FindingModal
          finding={editingFinding === 'new' ? null : editingFinding}
          onClose={closeModals}
          onSave={saveFinding}
          saving={saving}
          error={saveError}
        />
      )}
      {editingSections && (
        <SectionsModal content={content} onClose={closeModals} onSave={(sections) => saveContent({ ...content, ...sections })} saving={saving} error={saveError} />
      )}
      {showHistory && <HistoryModal reportId={report.id} onClose={() => setShowHistory(false)} />}
    </div>
  );
};

export default AuditReportBody;
